import type { StrapiLaboratoryResultGridRow } from '../../api/strapiCrm';
import {
  emptyLaboratoryResultListFilters,
  type LaboratoryResultListFilters,
} from '../../api/strapiCrm';

export type { LaboratoryResultListFilters };
export type LaboratoryResultListGridRow = StrapiLaboratoryResultGridRow;

export const EMPTY_LABORATORY_RESULT_LIST_FILTERS: LaboratoryResultListFilters = {
  ...emptyLaboratoryResultListFilters,
};

export function formatLaboratoryGridDate(iso?: string | null): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.trim();
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Jakarta',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  const date = `${pick('day')}/${pick('month')}/${pick('year')}`;
  const time = `${pick('hour').padStart(2, '0')}:${pick('minute').padStart(2, '0')}`;
  return `${date} ${time}`;
}

/** Kode sampel berita acara: job number + list_order (contoh: GL-00001-1). */
export function formatLaboratoryResultJobNumber(row: StrapiLaboratoryResultGridRow): string {
  const job = row.work_order_job_number?.trim() || '';
  const listOrder = Number(row.list_order);
  const hasOrder = Number.isFinite(listOrder) && listOrder > 0;
  if (job && hasOrder) return `${job}-${listOrder}`;
  if (hasOrder && row.point_label?.trim()) return row.point_label.trim();
  const pointIndex = Number(row.point_index);
  const hasPoint = Number.isFinite(pointIndex) && pointIndex > 0;
  if (job && hasPoint) return `${job}-${pointIndex}`;
  if (row.point_label?.trim()) return row.point_label.trim();
  if (job) return job;
  if (hasPoint) return String(pointIndex);
  return row.work_order_label?.trim() || '—';
}

export function laboratoryResultListFiltersActive(filters: LaboratoryResultListFilters): boolean {
  return (
    filters.jobNumber.trim() !== '' ||
    filters.parameterName.trim() !== '' ||
    filters.rpsApprovedDate.trim() !== '' ||
    filters.testingDoneDate.trim() !== '' ||
    filters.bakuMutu.trim() !== '' ||
    filters.jenisSampel.trim() !== '' ||
    filters.analisTypes.some((value) => value.trim() !== '')
  );
}

/** Sent when admin analis lab has no assigned master, so the grid matches nothing. */
export const UNASSIGNED_ANALIS_TYPE_FILTER = '__unassigned_analis_type__';

export function isAdminAnalisLabUser(user: { userTypeCode?: string | null } | null | undefined): boolean {
  return user?.userTypeCode?.trim().toLowerCase() === 'admin_analis_lab';
}

/** Locked grid filter for admin analis lab. Null for every other user type. */
export function lockedAnalisTypeFilter(
  user: { userTypeCode?: string | null; analisTypeDataTitles?: string[] | null } | null | undefined,
): string[] | null {
  if (!isAdminAnalisLabUser(user)) return null;
  const titles = (user?.analisTypeDataTitles ?? []).map((title) => title.trim()).filter(Boolean);
  return titles.length > 0 ? titles : [UNASSIGNED_ANALIS_TYPE_FILTER];
}

export function analisTypeListMatches(types: string[] | undefined, assigned: string[]): boolean {
  const wanted = assigned.map((value) => value.trim().toLocaleLowerCase('id')).filter(Boolean);
  if (wanted.length === 0) return true;
  return (types ?? []).some((type) => wanted.includes(type.trim().toLocaleLowerCase('id')));
}

export function withLockedAnalisTypeFilter<T extends LaboratoryResultListFilters>(
  filters: T,
  lockedAnalisTypes: string[] | null,
): T {
  if (lockedAnalisTypes == null) return { ...filters, analisTypes: [] };
  return { ...filters, analisTypes: lockedAnalisTypes };
}
